import React, { useState, useEffect } from 'react';
import { Package, DollarSign, Clock, MapPin, ChevronRight, Calendar, Search, ChevronDown, ChevronUp, TrendingUp, Loader2, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { sessionsAPI } from '../services/api';

// Custom icons for map
const startIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background: #22c55e; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const endIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background: #ef4444; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Component to fit map to route bounds
const FitBounds = ({ routePath }) => {
  const map = useMap();
  useEffect(() => {
    if (routePath && routePath.length > 0) {
      const bounds = L.latLngBounds(routePath);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, routePath]);
  return null;
};

const HistoryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();

  const filters = [
    { id: 'all', labelKey: 'history_filter_all' },
    { id: 'week', labelKey: 'history_filter_week' },
    { id: 'month', labelKey: 'history_filter_month' },
    { id: 'completed', labelKey: 'history_filter_completed' },
  ];

  useEffect(() => {
    const fetchSessions = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await sessionsAPI.getMySessions();
        // Map API response to expected format
        const mappedSessions = response.data.map(session => ({
          id: session.id,
          title: session.job_title,
          client: session.client,
          date: new Date(session.created_at).toISOString().split('T')[0],
          time: new Date(session.created_at).toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          duration: session.duration,
          deliveries: session.deliveries_count,
          earnings: session.earnings,
          distance: session.distance,
          status: session.status,
          address: session.job_title,
          flyerType: 'Delivery',
          mapCenter: session.map_center || [40.7128, -74.0060],
          routePath: session.route_path || []
        }));
        setSessions(mappedSessions);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [isAuthenticated, language]);

  // Filter sessions
  const filteredJobs = sessions.filter(job => {
    if (searchQuery) {
      return job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             job.client.toLowerCase().includes(searchQuery.toLowerCase());
    }
    if (activeFilter === 'all') return true;
    if (activeFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(job.date) >= weekAgo;
    }
    if (activeFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      return new Date(job.date) >= monthAgo;
    }
    if (activeFilter === 'completed') return job.status === 'approved' || job.status === 'paid';
    return true;
  });

  // Calculate summary stats
  const totalDeliveries = filteredJobs.reduce((sum, job) => sum + job.deliveries, 0);
  const totalEarnings = filteredJobs.reduce((sum, job) => sum + job.earnings, 0);
  const totalDistance = filteredJobs.reduce((sum, job) => sum + job.distance, 0);

  const toggleExpand = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 text-xs">{t('history_paid') || 'Paid'}</Badge>;
      case 'approved':
        return <Badge className="bg-blue-100 text-blue-700 text-xs">{t('history_approved') || 'Approved'}</Badge>;
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">{t('history_pending') || 'Pending'}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 text-xs">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-gray-500">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="history-page">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-4 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900 mb-4" data-testid="history-title">{t('history_title')}</h1>
        
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder={t('history_search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 py-2.5 bg-gray-100 border-0 rounded-xl text-sm"
            data-testid="history-search-input"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              data-testid={`history-filter-${filter.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeFilter === filter.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t(filter.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-5 mt-5">
        <Card className="bg-gradient-to-br from-orange-500 to-red-600 border-0 p-4 text-white" data-testid="history-summary">
          <h3 className="text-sm font-medium text-orange-100 mb-3">{t('history_total_summary')}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-2xl font-bold">{totalDeliveries}</p>
              <p className="text-xs text-orange-100">{t('history_deliveries')}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">${totalEarnings.toFixed(0)}</p>
              <p className="text-xs text-orange-100">{t('history_earned')}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{totalDistance.toFixed(1)}</p>
              <p className="text-xs text-orange-100">{t('history_miles')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* No History State */}
      {filteredJobs.length === 0 && (
        <div className="px-5 mt-12 text-center">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('history_no_history')}</h3>
          <p className="text-gray-500 text-sm">{t('history_start_first')}</p>
        </div>
      )}

      {/* Jobs List */}
      <div className="px-5 mt-6 pb-24">
        <h2 className="text-lg font-bold text-gray-900 mb-4" data-testid="recent-jobs-title">{t('history_recent_jobs')}</h2>
        <div className="space-y-4" data-testid="history-jobs-list">
          {filteredJobs.map((job, index) => (
            <Card
              key={job.id}
              className="bg-white border-0 shadow-sm overflow-hidden animate-slide-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`history-job-${job.id}`}
            >
              {/* Job Header - Clickable */}
              <div 
                className="p-4 cursor-pointer"
                onClick={() => toggleExpand(job.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{job.title}</h3>
                    <p className="text-sm text-gray-500">{job.client}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(job.status)}
                    {expandedJob === job.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <Calendar size={14} />
                  <span>{formatDate(job.date)}</span>
                  <span className="text-gray-300">|</span>
                  <span>{job.time}</span>
                </div>

                <div className="grid grid-cols-4 gap-2 bg-gray-50 rounded-xl p-3">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-orange-600">
                      <Package size={14} />
                      <span className="font-bold">{job.deliveries}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">{t('history_flyers')}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-blue-600">
                      <Clock size={14} />
                      <span className="font-bold">{formatDuration(job.duration)}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">{t('history_duration')}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-purple-600">
                      <TrendingUp size={14} />
                      <span className="font-bold">{job.distance.toFixed(1)}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">{t('history_miles')}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-green-600">
                      <DollarSign size={14} />
                      <span className="font-bold">${job.earnings.toFixed(2)}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">{t('history_earned')}</p>
                  </div>
                </div>
              </div>

              {/* Expanded Map Section */}
              {expandedJob === job.id && (
                <div className="border-t border-gray-100">
                  {job.routePath && job.routePath.length > 0 ? (
                    <>
                      {/* Route Map Preview */}
                      <div 
                        className="h-48 relative cursor-pointer"
                        onClick={() => setSelectedSession(job)}
                        data-testid={`view-map-${job.id}`}
                      >
                        <MapContainer
                          key={`history-map-${job.id}`}
                          center={job.routePath[0]}
                          zoom={15}
                          style={{ height: '100%', width: '100%' }}
                          zoomControl={false}
                          attributionControl={false}
                          dragging={false}
                          scrollWheelZoom={false}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <FitBounds routePath={job.routePath} />
                          
                          {/* Route path */}
                          <Polyline
                            positions={job.routePath}
                            pathOptions={{
                              color: '#f97316',
                              weight: 4,
                              opacity: 0.9,
                              lineCap: 'round',
                              lineJoin: 'round',
                            }}
                          />
                          
                          {/* Start marker */}
                          <Marker position={job.routePath[0]} icon={startIcon} />
                          
                          {/* End marker */}
                          <Marker position={job.routePath[job.routePath.length - 1]} icon={endIcon} />
                        </MapContainer>

                        {/* Map Legend */}
                        <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-3 text-xs z-[1000]">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-gray-600">{t('history_start')}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-gray-600">{t('history_end')}</span>
                          </div>
                        </div>

                        {/* Tap to expand overlay */}
                        <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs z-[1000]">
                          {t('history_tap_to_expand') || 'Tap to expand'}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="p-3 bg-gray-50">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <MapPin size={14} className="text-orange-500" />
                          <span>{job.address}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-6 text-center bg-gray-50">
                      <MapPin size={32} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">{t('history_no_route') || 'No GPS route recorded for this session'}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Full Screen Map Modal */}
      {selectedSession && selectedSession.routePath && selectedSession.routePath.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="route-map-modal">
          {/* Modal Header */}
          <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{selectedSession.title}</h3>
              <p className="text-xs text-gray-400">{formatDate(selectedSession.date)} • {selectedSession.time}</p>
            </div>
            <button
              onClick={() => setSelectedSession(null)}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center"
              data-testid="close-route-modal-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Full Map */}
          <div className="flex-1 relative">
            <MapContainer
              key={`fullscreen-map-${selectedSession.id}`}
              center={selectedSession.routePath[0]}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds routePath={selectedSession.routePath} />
              
              {/* Route path */}
              <Polyline
                positions={selectedSession.routePath}
                pathOptions={{
                  color: '#f97316',
                  weight: 5,
                  opacity: 0.9,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              />
              
              {/* Start marker */}
              <Marker position={selectedSession.routePath[0]} icon={startIcon} />
              
              {/* End marker */}
              <Marker position={selectedSession.routePath[selectedSession.routePath.length - 1]} icon={endIcon} />
            </MapContainer>

            {/* Map Legend */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg z-[1000]">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-700">{t('history_start')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-700">{t('history_end')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 rounded bg-orange-500"></div>
                  <span className="text-sm text-gray-700">{t('history_route') || 'Route'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Session Stats Footer */}
          <div className="bg-gray-900 text-white p-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-orange-400">{selectedSession.deliveries}</p>
                <p className="text-xs text-gray-400">{t('history_flyers')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{formatDuration(selectedSession.duration)}</p>
                <p className="text-xs text-gray-400">{t('history_duration')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{selectedSession.distance.toFixed(2)} mi</p>
                <p className="text-xs text-gray-400">{t('history_miles')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-green-400">${selectedSession.earnings.toFixed(2)}</p>
                <p className="text-xs text-gray-400">{t('history_earned')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
